<template>
    <div class="screen-saver" :class="{ visible: isVisible }">
        <div class="screen-saver-content">
            <img
                v-for="(image, index) in images"
                :key="index"
                :src="image"
                :class="{
                    active: currentImageIndex === index && isVisible,
                    previous: previousImageIndex === index && isVisible,
                    entering: enteringIndex === index && isVisible && currentImageIndex !== index,
                    'no-transition': isInitializing && index === 0,
                }"
                :style="getImageStyle(index)"
                class="screen-saver-image"
                alt=""
            />
            <div v-if="images.length === 0" class="screen-saver-content-text">
                <h1>No images found</h1>
                <p>Please add images to the screen saver config</p>
            </div>
        </div>

        <!-- the call to action does not handle the click itself: it lets it bubble to the screen
             saver, which is what dismisses it -->
        <button type="button" class="tap-to-start">{{ translations.kiosk.tapToStart }}</button>

        <!-- lets the guest pick a language before starting an order. a click on the screen saver
             dismisses it, so the click is stopped here to keep the selector usable -->
        <LanguageSelector v-if="isVisible" @click.stop />
    </div>
</template>

<script setup lang="ts">
    import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";

    import LanguageSelector from "@/app/components/language/LanguageSelector.vue";
    import { translations } from "@/shared/localization/translations";

    const props = defineProps<{
        images: string[];
        isVisible: boolean;
        interval?: number;
    }>();

    const currentImageIndex = ref(0);
    const previousImageIndex = ref<number | null>(null);
    const enteringIndex = ref<number | null>(null);
    const isInitializing = ref(false);
    let imageInterval: number | null = null;
    let transitionTimeout: number | null = null;

    const intervalDuration = props.interval ?? 5000;
    const TRANSITION_DURATION = 800;

    function getImageStyle(index: number) {
        let zIndex = 0;

        if (currentImageIndex.value === index && props.isVisible) {
            zIndex = 10000;
        } else if (previousImageIndex.value === index && props.isVisible) {
            zIndex = 9999;
        } else if (enteringIndex.value === index && props.isVisible) {
            zIndex = 10000;
        }

        return { zIndex };
    }

    function startScreenSaver() {
        if (props.images.length === 0) return;

        isInitializing.value = true;
        currentImageIndex.value = 0;
        previousImageIndex.value = null;
        enteringIndex.value = null;

        nextTick(() => {
            requestAnimationFrame(() => {
                isInitializing.value = false;
            });
        });

        imageInterval = window.setInterval(() => {
            transitionToNext();
        }, intervalDuration);
    }

    function transitionToNext() {
        if (props.images.length === 0) return;

        const nextIndex = (currentImageIndex.value + 1) % props.images.length;
        const currentIndex = currentImageIndex.value;

        if (transitionTimeout !== null) {
            clearTimeout(transitionTimeout);
        }

        previousImageIndex.value = currentIndex;
        enteringIndex.value = nextIndex;

        nextTick(() => {
            requestAnimationFrame(() => {
                currentImageIndex.value = nextIndex;

                transitionTimeout = window.setTimeout(() => {
                    previousImageIndex.value = null;
                    enteringIndex.value = null;
                }, TRANSITION_DURATION);
            });
        });
    }

    function stopScreenSaver() {
        if (imageInterval !== null) {
            clearInterval(imageInterval);
            imageInterval = null;
        }
        if (transitionTimeout !== null) {
            clearTimeout(transitionTimeout);
            transitionTimeout = null;
        }

        currentImageIndex.value = 0;
        previousImageIndex.value = null;
        enteringIndex.value = null;
        isInitializing.value = false;
    }

    watch(
        () => props.isVisible,
        (newVal) => {
            if (newVal) {
                currentImageIndex.value = 0;
                startScreenSaver();
            } else {
                stopScreenSaver();
            }
        }
    );

    watch(
        () => props.images,
        () => {
            if (props.isVisible) {
                stopScreenSaver();
                startScreenSaver();
            }
        },
        { deep: true }
    );

    onMounted(() => {
        if (props.isVisible) {
            startScreenSaver();
        }
    });

    onUnmounted(() => {
        stopScreenSaver();
    });
</script>

<style lang="scss" scoped>
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    .screen-saver {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        z-index: 9999;
        background: #000;
        opacity: 0;
        visibility: hidden;
        transition:
            opacity 0.2s ease-in-out,
            visibility 0.2s ease-in-out;

        &.visible {
            opacity: 1;
            visibility: visible;
        }
    }

    .screen-saver-content {
        position: relative;
        width: 100%;
        height: 100%;
        overflow: hidden;
    }

    .screen-saver-image {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: cover;
        will-change: transform, opacity;
        opacity: 0;
        transform: translateX(100%);
        transition:
            transform 0.8s variables.$easeOutExpo,
            opacity 0.5s variables.$easeOutExpo;

        &.previous {
            opacity: 1;
            transform: translateX(0);
        }

        &.entering {
            opacity: 0;
            transform: translateX(100%);
        }

        &.active {
            opacity: 1;
            transform: translateX(0);
        }

        &.no-transition {
            transition: none;
        }
    }

    // everything here is in vw so the button keeps its proportions on any kiosk display,
    // whatever its resolution or orientation. the box follows its text, so the font size is
    // picked from the longest translation - the french one is 13.0em wide in Open Sans bold,
    // so 6vw puts it at 78vw and the whole button at 91vw. the italian and german ones make
    // it 72vw wide, the english one 51vw
    //
    // the pill of the app buttons, .paybtn above all: the radius is half the height - 13.2vw
    // here, a 7.2vw line box between two 3vw paddings - and the text is inset by the radius
    // so it clears the rounded ends
    .tap-to-start {
        position: absolute;
        // a fifth of the display, but never so low that it runs into the language selector in
        // the corner: that one is 40px off the bottom and 96px tall (KioskModeConfiguration.vue),
        // so 136px of it plus a 20px gap is the floor - it only takes over below 780px of height
        bottom: max(20%, 156px);
        left: 50%;
        transform: translateX(-50%);
        padding: 3vw 6.6vw;
        border: none;
        border-radius: 6.6vw;
        background-color: var(--colorPrimary);
        color: #fff;
        font-size: 6vw;
        font-weight: 700;
        line-height: 1.2;
        white-space: nowrap;
        cursor: pointer;
        // the rotating images carry an inline z-index of 10000, same as the language selector
        z-index: 10001;
    }
</style>
